import React, { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import './TableComponent.scss';

export interface TableColumn<T = any> {
  id: string;
  label: string | ReactNode;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  width?: number;
  className?: string;
  sortable?: boolean;
  onSort?: () => void;
  sortDirection?: 'asc' | 'desc' | null;
  render?: (row: T, index: number) => ReactNode;
}

export interface TableFooterChip {
  label: string;
  value: string | number;
  variant?: 'filled' | 'outlined';
}

export interface TableComponentProps<T = any> {
  // Data
  data: T[];
  columns: TableColumn<T>[];

  // Display
  title?: string;
  subtitle?: string | ((data: T[]) => string);
  size?: 'small' | 'medium';
  className?: string;
  containerClassName?: string;
  headerClassName?: string;
  emptyMessage?: string;

  // Loading
  loading?: boolean;
  loadingMessage?: string;

  // Selection
  selectable?: boolean;
  selectedIndices?: number[];
  selectAll?: boolean;
  onToggleRow?: (index: number | number[]) => void;
  onToggleSelectAll?: () => void;
  getCheckboxState?: (row: T, index: number) => {
    checked: boolean;
    indeterminate?: boolean;
  };

  // Pagination
  paginated?: boolean;
  page?: number;
  rowsPerPage?: number;
  total?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions?: number[];

  // Footer
  footerChips?: TableFooterChip[];
  customFooter?: ReactNode;

  // Misc
  rowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T, index: number) => void;
  stickyHeader?: boolean;
  maxHeight?: string | number;
  disablePaper?: boolean;
}

export function TableComponent<T = any>({
  data,
  columns,
  title,
  subtitle,
  size = 'small',
  className = '',
  containerClassName = '',
  headerClassName = '',
  emptyMessage = 'No data available',
  loading = false,
  loadingMessage = 'Loading...',
  selectable = false,
  selectedIndices = [],
  selectAll = false,
  onToggleRow,
  onToggleSelectAll,
  getCheckboxState,
  paginated = false,
  page = 0,
  rowsPerPage = 10,
  total,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  footerChips,
  customFooter,
  rowKey,
  onRowClick,
  stickyHeader = false,
  maxHeight,
  disablePaper = false,
}: TableComponentProps<T>) {
  const getRowKey = (row: T, index: number): string | number => {
    if (rowKey) {
      return rowKey(row, index);
    }
    return (row as any).id ?? index;
  };

  const getCellValue = (column: TableColumn<T>, row: T, index: number): ReactNode => {
    if (column.render) {
      return column.render(row, index);
    }
    return (row as any)[column.id];
  };

  const getSubtitleText = (): string => {
    if (!subtitle) return '';
    if (typeof subtitle === 'function') {
      return subtitle(data);
    }
    return subtitle;
  };

  const renderSortIndicator = (column: TableColumn<T>) => {
    if (!column.sortable || !column.sortDirection) return null;
    return column.sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const totalColumnsCount = selectable ? columns.length + 1 : columns.length;

  const isAllSelected = data.length > 0 && selectedIndices.length === data.length;
  const isSomeSelected = selectedIndices.length > 0 && selectedIndices.length < data.length;

  const tableContent = (
    <>
      {/* Header with Title and Subtitle */}
      {(title || subtitle) && (
        <Box className={`table-title-container ${headerClassName}`}>
          {title && (
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {getSubtitleText()}
            </Typography>
          )}
        </Box>
      )}

      <TableContainer
        className={`table-content-wrapper ${containerClassName}`}
        style={{ maxHeight }}
      >
        <Table size={size} stickyHeader={stickyHeader}>
          <TableHead className={`table-head ${headerClassName}`}>
            <TableRow>
              {selectable && (
                <TableCell>
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={onToggleSelectAll}
                    disabled={loading}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{
                    minWidth: column.minWidth,
                    width: column.width,
                    cursor: column.sortable ? 'pointer' : 'default',
                  }}
                  className={column.className}
                  onClick={column.sortable ? column.onSort : undefined}
                >
                  {column.label}
                  {renderSortIndicator(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumnsCount} align="center">
                  <CircularProgress size={24} />
                  {loadingMessage && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {loadingMessage}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumnsCount} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => {
                const checkboxState = getCheckboxState
                  ? getCheckboxState(row, index)
                  : { checked: selectedIndices.includes(index), indeterminate: false };

                return (
                  <TableRow
                    key={getRowKey(row, index)}
                    hover={!!onRowClick}
                    onClick={() => onRowClick?.(row, index)}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                  >
                    {selectable && (
                      <TableCell>
                        <Checkbox
                          checked={checkboxState.checked}
                          indeterminate={checkboxState.indeterminate}
                          onChange={() => onToggleRow?.(index)}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || 'left'}
                        className={column.className}
                      >
                        {getCellValue(column, row, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {paginated && (
        <TablePagination
          component="div"
          count={total ?? data.length}
          page={page}
          onPageChange={onPageChange || (() => { })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange || (() => { })}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}

      {/* Footer with Chips */}
      {footerChips && footerChips.length > 0 && (
        <Box className="table-footer-chips">
          {footerChips.map((chip, index) => (
            <Chip
              key={index}
              label={`${chip.label}: ${chip.value}`}
              variant={chip.variant || 'outlined'}
            />
          ))}
        </Box>
      )}

      {/* Custom Footer */}
      {customFooter && <Box className="table-custom-footer">{customFooter}</Box>}
    </>
  );

  if (disablePaper) {
    return <Box className={`table-component ${className}`}>{tableContent}</Box>;
  }

  return (
    <Box className={`table-component table-container ${className}`}>
      {tableContent}
    </Box>
  );
}
